import { useEffect, useState } from "react";
import type { ScheduleDTO, UserDTO } from "../../shared/types";
import { useUser } from "../App";
import { get, post } from "../lib/api";
import { Dialog } from "./Dialog";
import { Button, ErrorText, Field, inputCls } from "./ui";

/**
 * "Hand over to…" (AIT-62): one person picked from the current people, never the person looking or the one it runs as.
 * They get a card in their Requests and their Pandito chat; it becomes theirs only when they accept it, and until they
 * answer it does not run. Whether they may have it is the server's to say, in its own words.
 */
export function HandoverDialog({ schedule, onDone, onClose }: { schedule: ScheduleDTO; onDone: () => void; onClose: () => void }) {
  const me = useUser();
  const [people, setPeople] = useState<UserDTO[] | null>(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    get<UserDTO[]>("/api/users").then(
      (all) => setPeople(all.filter((p) => p.kind === "person" && !p.retiredAt && p.id !== me.id && p.id !== schedule.ownerUserId).sort((x, y) => x.name.localeCompare(y.name))),
      (err) => setError((err as Error).message),
    );
  }, [me.id, schedule.ownerUserId]);

  const offer = async () => {
    setBusy(true);
    setError(null);
    try {
      await post(`/api/schedules/${schedule.id}/offer`, { userId: to });
      onDone();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      title={`Hand over "${schedule.name}"`}
      description="They get a card in their Requests and their Pandito chat. Once they accept, it runs as them, with their own logins. Until they answer, it does not run."
      busy={busy}
      primary={
        <Button variant="primary" disabled={busy || !to} onClick={offer}>
          {busy ? "Offering…" : "Offer it"}
        </Button>
      }
    >
      <div className="space-y-3">
        <Field label="To" hint={`They take it over on ${schedule.agentName ?? "its agent"}; you no longer run it once they accept.`}>
          <select className={inputCls} value={to} disabled={busy || !people} onChange={(e) => setTo(e.target.value)} aria-label="The person it is handed to">
            <option value="">{people ? "Choose a person" : "Loading people…"}</option>
            {(people ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.role === "member" ? " (member)" : ""}
              </option>
            ))}
          </select>
        </Field>
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

/** The line on an offered schedule's row (AIT-62): whom it waits for, and Withdraw for whoever may take it back. */
export function OfferLine({ schedule, canWithdraw, busy, onWithdraw }: { schedule: ScheduleDTO; canWithdraw: boolean; busy: boolean; onWithdraw: () => void }) {
  if (!schedule.offer) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-warning">
      <span title="It does not run until they answer">Waiting for {schedule.offer.toName} to accept</span>
      {canWithdraw && <span className="text-faint">·</span>}
      {canWithdraw && (
        <Button variant="ghost" size="sm" disabled={busy} onClick={onWithdraw}>
          Withdraw
        </Button>
      )}
    </div>
  );
}
